<template>
	<VDialogV3
		position="right"
		width="600px"

    :entity="localAssembly"

		@submit="onSubmit"
	>
		<section>
			<VSelectV2
				v-model="localAssembly.orderCategory"
				label="Категория"
				:disabled="!isNewDocument"
				layout="vertical"
				:data="$core.dictionaries.orderCategories"
			/>
		</section>

		<section>
			<VSelectV2
				v-model="localAssembly.seller"
				label="Грузоотправитель"
				layout="vertical"
				required
				hide-clear-btn
				:disabled="!isNewDocument || isSeller"
				:data="sellerSource"
			/>

			<VSelectV2
				v-model="localAssembly.sellerCompany"
				label="Юр. лицо"
				layout="vertical"
				hide-clear-btn
				:disabled="!isNewDocument"
				:data="sellerCompanySource"
			/>

			<VSelectV2
				v-model="localAssembly.sellerStore"
				label="Склад отправки"
				layout="vertical"
				hide-clear-btn
				:disabled="localAssembly.isTransportOrderCreated || !sellerStoreHasTransportZone"
				:data="sellerStoreSource"
			/>
		</section>

		<section>
			<!-- скрывает покупатель-склад от seller при создании сборочного листа -->
			<template v-if="isSeller">
				<VSelectV2
					v-model="localAssembly.buyerCompany"
					label="Юр. лицо грузополучателя"
					layout="vertical"
					disabled
					:data="buyerCompanySource"
				/>
			</template>
			<template v-else>
				<VSelectV2
					v-model="localAssembly.buyer"
					label="Грузополучатель"
					layout="vertical"
					required
					disabled
				/>
				<VSelectV2
					v-model="localAssembly.buyerCompany"
					label="Юр. лицо"
					layout="vertical"
					hide-clear-btn
					:disabled="!isNewDocument"
					:data="buyerCompanySource"
				/>
				<VSelectV2
					v-model="localAssembly.buyerStore"
					label="Склад доставки"
					layout="vertical"
					hide-clear-btn
					:data="buyerStoreSource"
				/>
			</template>
		</section>

		<section>
			<VDateInput
				v-model="localAssembly.assemblyDate"
				label="Дата готовности"
				layout="vertical"
				required
			/>
		</section>

		<hr>

		<section>
			<h1>Условия доставки</h1>
			<VSelectV2
				v-model="localAssembly.deliveryType"
				label="Тип доставки"
				layout="vertical"
				:data="DeliveryTypesService.getListAccessibleByBuyer()"
				disabled
			/>
		</section>

		<section>
			<VTemperatureRangeInput
				v-if="localAssembly.orderCategory?.id === 'rawMaterialsAndPackage'"
				v-model="localAssembly.storeTemperatureRange"
				label="Температурный режим перевозки, °C"
				:precision="0"
				required
				:min="-60"
				:max="70"
        :with-asterisk="true"
			/>
		</section>

		<section class="delivery-info">
			<header>Пункт отгрузки</header>
			<template v-if="localAssembly.sellerStore">
				<div
					class="text-wrap"
					:title="localAssembly.sellerStore.name"
				>
					{{ localAssembly.sellerStore.name || '---' }}
				</div>
				<footer v-if="localAssembly.sellerStore.workingTime">
					Режим работы: {{ localAssembly.sellerStore.workingTime }}
				</footer>
			</template>
			<template v-else>
				--
			</template>
		</section>

		<section class="delivery-info">
			<header>Пункт доставки</header>
			<template v-if="localAssembly.buyerStore">
				<div
					class="text-wrap"
					:title="localAssembly.buyerStore.name"
				>
					{{ localAssembly.buyerStore.name }}
				</div>
				<footer v-if="localAssembly.buyerStore.workingTime">
					Режим работы: {{ localAssembly.buyerStore.workingTime }}
				</footer>
				<footer v-if="localAssembly.buyerStore.notes">
					Особые требования: {{ localAssembly.buyerStore.notes }}
				</footer>
			</template>
			<template v-else>
				--
			</template>
		</section>

		<section>
			<VSelectV2
				v-model="localAssembly.responsible"
				required
				label="Ответственный"
				placeholder="Введите ФИО ответственного"
				layout="vertical"
				:data="responsibleSource"
			/>
		</section>
	</VDialogV3>
</template>

<script>

import PersonsService       from '@/domains/Persons/PersonsService';
import StoresService        from '@/domains/Stores/StoresService';
import PartnersService      from '@/domains/Partners/PartnersService';
import DeliveryTypesService from '@/domains/DeliveryTypes/DeliveryTypesService';
import CompaniesService     from '@/domains/Companies/CompaniesService';
import Core from "@/core/Core.ts";

export default {
	name: 'AssemblyRequisites',

	components: {
	},

	props: {
		assembly: {
			type:    Object,
			default: null,
		},
	},

	emits: ['updated'],

	data() {
		/**
		 * Правило прелоада зависимых источников документа:
		 * - новый — preloadOnMount: опции готовы до первого клика,
		 *   контрагентские подгрузятся сами при выборе партнёра;
		 * - существующий — preloadOnFocus: шанс, что реквизиты станут
		 *   менять, низок — впустую не грузим.
		 */
		const isNewDocument = this.$core.utils.isNullOrUndefined(this.assembly?.id);

		return {
			DeliveryTypesService,

			isNewDocument,
			localAssembly: this.$core.utils.cloneDeep(this.assembly),

			sellerSource: this.$core.loader.select(({ pattern }) => PartnersService.searchSellers(pattern)),

			sellerCompanySource: this.$core.loader.select(({ seller }) => CompaniesService.getList({ partnerId: seller.id }))
				.dependsOn({ seller: () => this.localAssembly.seller })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			/**
			 * Новый документ — транспортной зоны ещё нет, transportZoneId уйдёт
			 * null, и бэк вернёт все склады поставщика. Заполненный — зона уже
			 * установлена, выбрать можно только склады в её пределах. Поэтому
			 * зона читается из оригинала (проп assembly), а не из локальной
			 * копии, где пользователь мог уже сменить склад.
			 */
			sellerStoreSource: this.$core.loader.select(({ seller }) => StoresService.getList({
					partnerId:       seller.id,
					// transportZoneId: this.assembly?.sellerStore?.transportZone?.id,
				}))
				.dependsOn({ seller: () => this.localAssembly.seller })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			buyerCompanySource: this.$core.loader.select(({ buyer }) => CompaniesService.getList({ partnerId: buyer.id }))
				.dependsOn({ buyer: () => this.localAssembly.buyer })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			buyerStoreSource: this.$core.loader.select(({ buyer }) => StoresService.getList({ partnerId: buyer.id }))
				.dependsOn({ buyer: () => this.localAssembly.buyer })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

      /** Ответственный ищется по паттерну в пределах партнёра-покупателя.
       *  buyer читается лениво: на момент создания источника потребность
       *  ещё не загружена (init отрабатывает в mounted). */
      responsibleSource: this.$core.loader.select(({pattern}) => PersonsService.searchPersons(pattern, this.localAssembly.buyer.id))
        .dependsOn({ buyer: () => this.localAssembly.buyer })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false)
        .default((options) => options.find(person => person.id === Core.currentUser.id) ?? options[0]),
    };
	},

	computed: {
		isSeller() {
			return this.$core.currentPartner.isSeller;
		},

		sellerStoreHasTransportZone() {
			return this.localAssembly.sellerStore?.transportZone?.id != null;
		},
	},

	methods: {
		async onSubmit() {
			this.$emit('updated', this.localAssembly);
			this.$core.dialogs.AssemblyRequisites.hide();
		},
	},
};
</script>
